/**
 * ONE ATTACK, AGAINST ONE DEFENDER, AS A PURE FUNCTION.
 *
 * The hit loop used to live inside `useAttackExecution.resumeAttackFromHit`,
 * welded to a single `tab` and to React state a hundred lines either side of
 * it. Nothing in the loop needs any of that: `calcSingleHitDamage` already
 * takes its target as a plain argument and is bound to nothing, and the only
 * state the loop carries is a running wound total and a force field that may
 * fail partway through a burst.
 *
 * Pulling it out buys two things. The obvious one is that an area weapon can
 * now run it once per victim - five people caught by a grenade is five calls,
 * not a rewrite. The quieter one is that the most arithmetic-heavy part of the
 * combat engine becomes testable without mounting a component.
 *
 * WHAT STAYS IN THE HOOK: dice for the attack roll, narrative, the defence
 * prompt, ammunition, everything that talks to the session. This function
 * knows about one defender and the numbers already worked out about the shot.
 */
import { getBodyConfig, getHitLocationForBody } from './bodyConfig';
import { burstLocation } from './multipleHits';
import { HIT_LOCATIONS } from './constants';
import { calcSingleHitDamage } from './damageCalc';
import type { CalcSingleHitParams, HitResult } from './damageCalc';
import type { BodyConfig } from './bodyConfig';
import type { BodyTypeId, HitLocation, Slot, Target } from '../types';
import type { AveragedDamageMode } from './survival/averagedDamage';

export interface ResolveHitsParams {
  slot: Slot;
  /** The defender as projected FOR THIS ATTACKER - cover and facing included. */
  target: Target;
  /** How many hits the fire mode and DoS produced. */
  numHits: number;
  dos: number;
  /** Effective penetration, Hammer Blow and Razor Sharp already folded in. */
  pen: number;
  accurateBonus: number;
  rangeBand: string;
  isMelee: boolean;
  isShortOrCloser: boolean;
  /** A location id, or `'auto'` to derive the first from the attack roll. */
  targetLocation: string;
  /** Decides the first hit location when aiming is left on `'auto'`. */
  attackRoll: number;
  /** Wounds healed by regeneration this turn, added before the loop. */
  regenHealed?: number;
  hatred?: boolean;
  autoConfirmRF?: boolean;
  damageBonusMelee?: number;
  damageBonusRanged?: number;
  rerollDamage?: boolean;
  /** The target is helpless against this attack: damage is rolled twice (engine/helpless). */
  helpless?: boolean;
  /** Whether the table has switched the protagonist rules on. Absent means off. */
  protagonistRules?: boolean;
  /** The table's averaged-damage ruling. Absent means off. */
  averagedDamage?: AveragedDamageMode;
  /**
   * An arc echo (v8.372.3; Arcing's blunted echo since v8.291.0): the
   * falloff scales the RAW roll inside calcSingleHitDamage, and unless
   * `echoesCrit` says otherwise the echo scores no crit and no fury - the
   * Cleaving idiom. `dice` mode rolls the echo's own expression in place of
   * the weapon's: the same dice count the primary drew, another expression.
   */
  falloff?: { mode: 'percent' | 'flat' | 'fixed' | 'dice'; value: number; floor: number; dice: string; leap: number };
  echoesCrit?: boolean;
  /** A passed avoid test's half or quarter on the raw roll (v8.372.3). */
  rawScale?: number;
  /** The template that caught THIS victim bypasses cover (v8.373.2); absent = the weapon block's. */
  coverBypass?: boolean;
  /** The attacker's team tag, for the target's `@scope` exposures (v8.350.0). */
  attackerTeamTag?: string | null;
  /** The attacker's side (2026-10-23); absent = the tag. */
  attackerSideTag?: string | null;
}

export interface ResolvedHits {
  hits: HitResult[];
  /** The defender's wounds after the burst, for callers that want to look ahead. */
  runningWounds: number;
}

/**
 * Roll every hit of one attack against one defender.
 *
 * TWO PIECES OF STATE CROSS HITS, and both matter:
 *
 *  - `runningWounds` falls as the burst lands, because damage past zero feeds
 *    critical severity. Starting it fresh per hit would make a five-round
 *    burst far less lethal than the rules intend.
 *  - A FORCE FIELD THAT OVERLOADS STAYS DOWN. Once a hit overloads it, every
 *    later hit in the same burst bypasses it - which is why the target is
 *    rebuilt inside the loop (`rollBurst`) rather than read once.
 *
 * Hit locations: the first is aimed or derived from the attack roll; on a
 * humanoid the rest follow it by the Multiple Hits table (engine/multipleHits,
 * no dice drawn), on other bodies they are rolled - unless the attacker called
 * a location, in which case every hit goes there.
 */
export function resolveHitsAgainst(params: ResolveHitsParams): ResolvedHits {
  const { target, numHits, targetLocation, regenHealed = 0 } = params;
  const echo = echoOf(params);
  const bodyType = target.bodyType || 'humanoid';
  const bodyConfig = getBodyConfig(bodyType);
  const firstLoc = firstLocation(bodyConfig, targetLocation, params.attackRoll);
  // The echo is blunted after the loop: it only clears crit fields, which no
  // later hit reads, so the dice and the running wounds are unchanged.
  const resolved = rollBurst({
    shared: sharedHitParams(params, echo, bodyType),
    target,
    numHits,
    wounds: target.wounds.current + regenHealed,
    locationAt: (i) => locationOf(i, firstLoc, targetLocation, bodyConfig),
  });
  if (echo.isEcho && !params.echoesCrit) resolved.hits.forEach(bluntEcho);
  return resolved;
}

/** Everything a hit passes to calcSingleHitDamage except what the burst loop fills in per hit. */
type BurstHitParams = Omit<CalcSingleHitParams, 'target' | 'location' | 'isFirstHit' | 'hitIndex' | 'runningWounds'>;

interface BurstParams {
  shared: BurstHitParams;
  target: Target;
  numHits: number;
  /** The defender's wounds as the first hit lands. */
  wounds: number;
  /** Hit `i`'s location; called once per hit, in order, just before its damage roll. */
  locationAt: (i: number) => HitLocation;
}

/**
 * THE burst loop, shared by the live roll and both forecasts so they cannot
 * drift apart: each hit takes its location (`locationAt`: the table or a roll), then its damage against the
 * running wounds and a force field that, once overloaded, stays down for the
 * rest of the burst. Draws dice only through `locationAt` and
 * calcSingleHitDamage, in that order per hit (gotcha #5).
 */
export function rollBurst({ shared, target, numHits, wounds, locationAt }: BurstParams): ResolvedHits {
  const hits: HitResult[] = [];
  let runningWounds = wounds;
  let effectiveTarget = target;
  for (let i = 0; i < numHits; i++) {
    const location = locationAt(i);
    const hit = calcSingleHitDamage({
      ...shared,
      target: effectiveTarget,
      location,
      isFirstHit: i === 0,
      hitIndex: i,
      runningWounds,
    });
    effectiveTarget = afterOverload(effectiveTarget, hit);
    runningWounds -= hit.effectiveDamage;
    hits.push(hit);
  }
  return { hits, runningWounds };
}

interface EchoSetup {
  isEcho: boolean;
  /** The slot the echo fires: the weapon, or in dice mode the weapon with the echo's expression. */
  slot: Slot;
  falloff: CalcSingleHitParams['falloff'];
}

/**
 * An arc echo's slot and falloff. An echo in dice mode fires the weapon
 * with the echo's expression; its floor still holds (a flat falloff of 0).
 */
function echoOf({ slot, falloff }: ResolveHitsParams): EchoSetup {
  if (!falloff || !(falloff.leap > 0)) return { isEcho: false, slot, falloff: undefined };
  const { mode, value, floor, leap } = falloff;
  if (mode !== 'dice') return { isEcho: true, slot, falloff: { mode, value, floor, leap } };
  const echoSlot = falloff.dice ? { ...slot, damageExpression: falloff.dice } : slot;
  return { isEcho: true, slot: echoSlot, falloff: { mode: 'flat', value: 0, floor, leap } };
}

/**
 * The first hit's location: the called one, or the one the attack roll
 * reads. A called id falls back through the humanoid table for older
 * saved profiles, then gives up gracefully rather than throwing mid-attack.
 */
function firstLocation(bodyConfig: BodyConfig, targetLocation: string, attackRoll: number): HitLocation {
  if (targetLocation === 'auto') return getHitLocationForBody(attackRoll, bodyConfig);
  return (
    bodyConfig.locations.find((l: HitLocation) => l.id === targetLocation) ||
    HIT_LOCATIONS.find((l: HitLocation) => l.id === targetLocation) ||
    bodyConfig.locations[0]
  );
}

/** What every hit of the burst passes to calcSingleHitDamage, defaults applied. */
function sharedHitParams(params: ResolveHitsParams, echo: EchoSetup, bodyType: BodyTypeId) {
  const { damageBonusMelee = 0, damageBonusRanged = 0, protagonistRules = false, averagedDamage = 'off' } = params;
  return {
    slot: echo.slot,
    pen: params.pen,
    dos: params.dos,
    accurateBonus: params.accurateBonus,
    rangeBand: params.rangeBand,
    isMelee: params.isMelee,
    isShortOrCloser: params.isShortOrCloser,
    hatred: params.hatred,
    autoConfirmRF: params.autoConfirmRF,
    damageBonusMelee,
    damageBonusRanged,
    rerollDamage: params.rerollDamage,
    helpless: params.helpless,
    bodyType,
    protagonistRules,
    averagedDamage,
    attackerTeamTag: params.attackerTeamTag,
    attackerSideTag: params.attackerSideTag,
    falloff: echo.falloff,
    rawScale: params.rawScale,
    coverBypass: params.coverBypass,
  };
}

/** A force field this hit overloaded stays down for the rest of the burst. */
function afterOverload(target: Target, hit: HitResult): Target {
  return hit.ffOverloaded ? { ...target, forceField: { ...target.forceField, active: false } } : target;
}

/**
 * Hit `i`'s location: a called location takes every hit, else the first is `firstLoc` and the rest follow it by the
 * Multiple Hits table (a body without the table rolls them).
 */
function locationOf(i: number, firstLoc: HitLocation, targetLocation: string, bodyConfig: BodyConfig): HitLocation {
  return targetLocation !== 'auto' ? firstLoc : burstLocation(i, firstLoc, bodyConfig);
}

/**
 * The blunted echo (v8.291.0): no crit, no fury - unless the block lets
 * echoes have them (v8.372.3). A hit the field stopped is left as it is.
 * In place.
 */
function bluntEcho(hit: HitResult): void {
  if (hit.ffBlocked) return;
  hit.crit = null;
  hit.critSev = 0;
  hit.righteousFury = false;
  hit.rfConfirmed = false;
}
